import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { workshops } from "../../../content";
import type { Workshop } from "../../../types/content";
import CertificateShowcase from "./CertificateShowcase";
import WorkshopGlimpse from "./WorkshopGlimpse";
import { resolvePublishedImages } from "../../../utils/workshopImages";
import type { ResolvedWorkshopImage } from "../../../utils/workshopImages";

/**
 * ============================================================================
 * Workshops & Training — the /workshops route (D-076).
 * ============================================================================
 *
 * Direct consumer of the content layer (no bridge, V.4). To change what this
 * page says, edit `src/content/data/workshops.json` — not this file.
 *
 * NEWEST FIRST, AND THE ORDER IS NOT THIS COMPONENT'S DOING. The loader sorts
 * on `date` descending, so a new entry appended anywhere in the JSON lands in
 * the right place without anyone remembering that the page is chronological.
 * A component that sorted would be a second place the rule lives.
 *
 * THE MONTH LABEL IS DERIVED FROM `date`, NOT STORED BESIDE IT. D-071's rule:
 * if the layer can settle it, derive it. A "Apr 2026" written into the JSON
 * next to "2026-04" is two facts that can disagree, and the one on screen is
 * the one nothing checks. `dateDetail` carries only what a month cannot —
 * "Jan 13-31, 40-hour program" — and is null on the three entries that have
 * nothing extra to say.
 *
 * ONE ENTRY IS DIFFERENT AND IT LOOKS DIFFERENT. The 2024 training is the only
 * one with a document behind it, so it carries the centred certificate
 * showcase. The other three are cards. That asymmetry is the evidence showing
 * through the design rather than a decoration applied to it.
 *
 * ----------------------------------------------------------------------------
 * THE CARD'S OWN LAYOUT, WIDENED 2026-09-01 (D-099)
 * ----------------------------------------------------------------------------
 * The card was a column of left-aligned text with a 96–128 px thumbnail strip
 * under it, on a face up to 1180 px wide — measured at 1280, the content filled
 * roughly the left 60% and the rest was empty. The owner asked for these two
 * surfaces to read as premium, and the specific complaint the geometry supports
 * is that one: a card that uses half its width looks unfinished rather than
 * spacious.
 *
 * Three changes, and none of them adds a fact:
 *
 *   · the meta line becomes a CHIP ROW — role, host, and a credential chip on
 *     the one entry that has a document. Every value is the layer's; the
 *     credential chip is derived from the same two-key gate that decides
 *     whether the showcase renders at all, so it cannot appear beside a
 *     certificate the page is not showing;
 *   · the glimpse becomes a RESPONSIVE GRID that fills the card — same three
 *     photographs, drawn at the size the card actually has, plus the remainder
 *     tile. **The image COUNT is unchanged**, which matters: these are
 *     204–269 kB frames and D-086 is this repository's record of seven of them
 *     racing on one page;
 *   · the description is width-limited to `max-w-3xl` so a 1180 px card does
 *     not set 150-character lines, which is a readability floor rather than a
 *     taste call.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044), so the search result cannot drift from the page's opening line.
 *
 * NO COUNT AND NO SPAN. Both belong to the layer, which computes them (D-066:
 * a standfirst ships in the meta description and is the one line on a page
 * that nothing checks — the martial-arts page opened with "two decades" against
 * a record spanning three years).
 */
export const WORKSHOPS_STANDFIRST =
	"Workshops delivered and training conducted — sessions run for engineers and for the organisation, with what each one covered and the credential where one was issued.";

/** "2026-04" -> "Apr 2026"; "2023" -> "2023". Parsed by hand, never through
 *  Date: `new Date("2026-04")` is read as UTC and renders as March west of
 *  Greenwich.
 *
 *  EXPORTED SINCE D-081 so the detail route and this one render a month the
 *  same way. A second copy of this six-line function is a second place the
 *  timezone bug above can come back, in a file whose author has not read this
 *  comment.
 *
 *  THE `style` PARAMETER IS WHY THERE IS NOW ONE COPY AND NOT TWO (D-099). The
 *  Home teaser rendered "April 2026" through a `monthYear` of its own —
 *  deliberately long, because a teaser card has the room and a reader meeting
 *  the section for the first time should not be given an abbreviation. That was
 *  a reasonable display choice implemented as a SECOND HAND-PARSE of the same
 *  field, carrying its own copy of both bugs this comment records. The display
 *  choice survives; the second parse does not.
 *
 *  THE YEAR-ONLY BRANCH IS NOT COSMETIC (D-092). Without it `month` is NaN,
 *  `names[NaN - 1]` is undefined, and the pill reads "undefined 2023" — in the
 *  page, in the detail standfirst and in that route's meta description. The
 *  loader rejects any shape neither branch handles, so this cannot silently
 *  render a third thing. */
export const monthLabel = (value: string, style: "short" | "long" = "short"): string => {
	const [year, month] = value.split("-").map(Number);
	if (!month) return String(year);
	const short = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const long = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December"
	];
	return `${(style === "long" ? long : short)[month - 1]} ${year}`;
};

/**
 * ----------------------------------------------------------------------------
 * THE WHOLE CARD IS CLICKABLE, AND IT IS STILL ONE LINK IN THE ACCESSIBILITY
 * TREE (D-081).
 * ----------------------------------------------------------------------------
 * The obvious implementation — wrap the `<article>` in a `<Link>` — is barred
 * here, and not on style grounds. This card contains the certificate
 * showcase, which contains a `<button>`, and one entry also renders a
 * lightbox trigger: an `<a>` wrapping a `<button>` is invalid HTML, and the
 * parser REWRITES it. CLAUDE.md carries that rule for a measured reason —
 * D-042 found exactly one element a parser rewrote and it disabled hydration
 * site-wide, costing every page a full client re-render.
 *
 * So the link is where it belongs semantically, on the heading, and it is
 * stretched over the card with `after:absolute after:inset-0`. That gives a
 * mouse the whole card as a hit area while a screen reader hears one link
 * whose accessible name is the workshop's title, and a keyboard gets one tab
 * stop rather than a card-sized one plus the real one inside it.
 *
 * `relative` on the article is what the overlay resolves against — `.card-shell`
 * declares no `position`, so without it the pseudo-element would stretch to the
 * nearest positioned ancestor, which is the page.
 *
 * AND THE SHOWCASE IS LIFTED BACK ABOVE IT. The overlay covers the card, the
 * certificate button included, so the button below it carries `relative z-10`.
 * Without that the picture opens the detail route instead of the lightbox —
 * a defect no screenshot shows and no geometry harness catches, which is why
 * it is verified by hit-testing the coordinates rather than by measuring the
 * boxes (the D-080 method for the 24px hamburger).
 */

/** How many frames the card previews before it stops and says how many remain. */
const GLIMPSE_COUNT = 3;

const WorkshopEntry = ({ workshop }: { workshop: Workshop }): JSX.Element => {
	const when = workshop.dateDetail
		? `${monthLabel(workshop.date)} (${workshop.dateDetail})`
		: monthLabel(workshop.date);
	const glimpse: ResolvedWorkshopImage[] = resolvePublishedImages(workshop.images);

	/* THE CREDENTIAL CHIP IS READ OFF THE SAME GATE THAT DRAWS THE SHOWCASE.
	   Not off `workshop.certificate` alone: an entry may carry a certificate
	   whose `status` is not `published`, and a chip saying "Credential" above a
	   card that shows none would be the label-beside-a-computed-value defect
	   D-066 records. One condition, used twice, in one expression. */
	const credential = workshop.certificate?.status === "published" ? workshop.certificate : null;

	return (
		<article className="card-shell relative mt-8 first:mt-10">
			<div className="card-face p-6 sm:p-8">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<h2 className="text-xl font-bold text-[var(--text-strong)]">
						<Link
							to={`/workshops/${workshop.slug}`}
							className="rounded-[4px] after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
						>
							{workshop.title}
						</Link>
					</h2>
					{/* The shared tinted-label vocabulary (D-061). */}
					<span className="pill-tint shrink-0 tabular-nums border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
						{when}
					</span>
				</div>

				{/* The chip row. `role` and `host` were one muted sentence; they are
				    two facts about the session and the card has the width to set
				    them as two. Nothing new is asserted — every string here is the
				    layer's, and the credential chip's condition is the showcase's. */}
				<div className="mt-3 flex flex-wrap items-center gap-2">
					<span className="pill-tint pill-tint-upper border-[var(--cat-violet-border)] bg-[var(--cat-violet-fill)] text-[var(--cat-violet-text)]">
						{workshop.role}
					</span>
					<span className="pill-tint border-[var(--border-pill)] bg-[var(--surface)] text-[var(--text-secondary)]">
						{workshop.host}
					</span>
					{credential && (
						<span className="pill-tint border-[var(--cat-emerald-border)] bg-[var(--cat-emerald-fill)] text-[var(--cat-emerald-text)]">
							Credential
						</span>
					)}
				</div>

				<p className="mt-4 max-w-3xl text-sm leading-relaxed text-[var(--text-secondary)]">
					{workshop.description}
				</p>

				{/* The glimpse fills the card's width rather than sitting in the
				    left corner of it. Same three photographs and the same
				    remainder — the grid is the only thing that changed. */}
				<WorkshopGlimpse
					images={glimpse}
					skip={0}
					max={GLIMPSE_COUNT}
					className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
					frameClassName="block aspect-[4/3] overflow-hidden rounded-[10px]"
				/>

				{/* `relative z-10` — see the note above the component. The
				    stretched overlay sits over everything in this card, so the
				    certificate's own button has to be lifted back above it or
				    clicking the picture navigates instead of enlarging it. */}
				{workshop.certificate && (
					<div className="relative z-10">
						<CertificateShowcase certificate={workshop.certificate} statement={workshop.statement} />
					</div>
				)}

				{/* The affordance. A card that navigates should look like one —
				    `aria-hidden`, because the heading link above already carries
				    this destination and announcing it twice gives a screen-reader
				    user two links to one page. */}
				<p
					aria-hidden="true"
					className="mt-6 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200"
				>
					Read more →
				</p>
			</div>
		</article>
	);
};

export default function Workshops(): JSX.Element {
	return (
		<section className="section-shell" aria-labelledby="workshops-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Teaching &amp; Enablement</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="workshops-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Workshops
					</span>{" "}
					&amp; Training
				</h1>

				<p className="section-standfirst">{WORKSHOPS_STANDFIRST}</p>

				{workshops.map((workshop: Workshop) => (
					<WorkshopEntry key={workshop.id} workshop={workshop} />
				))}

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/achievements"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						Recognition →
					</Link>
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
